/**
 * FE line → wire line, for estimates and orders. One mapper per shape, shared by create AND update
 * (they used to be copy-pasted inline), each with the package branch first.
 *
 * Non-package branches are reproduced exactly from the old inline code — nothing about tray, custom
 * or add-on lines changes (covered by a byte-identical test).
 *
 * Package line contract: `{ type:'package', itemId:<packageId>, packageVariantId?, servesCount?, quantity,
 * unitPrice, notes?, sortOrder, isTaxExempt?, packageItems }`
 *  - `unitPrice` is ALWAYS sent (0 is legal; it overrides the variant price and is stored);
 *  - no `name`; `packageVariantId` omitted when null (BE then uses the package's default variant);
 *  - `servesCount` is TEXT (max 30): omitted (undefined) = the variant's serves, blank/null is sent as '' and stores null;
 *  - `packageItems` ALWAYS sent, even `[]` — omitting it makes BE re-copy the variant's list.
 *  Responses also carry read-only `packageVariantName` and `packageTagIds` (never sent back).
 */
import type { ApiEstimateDetail, ApiOrderLineItem, ApiOrderWireItem, UpdateLineItemBody } from '../../../services/cateringApi';
import { normalizePackageItems } from './packageItems';
import type { OrderPayloadLineItem } from '../../../redux/catering/cateringTypes';
import type { EstimateLineItem, OrderLineItem } from '../data/types';
import { toLinePackageItems } from './packageLines';

// ── API → FE (package lines only; the saga keeps the existing mapping for every other line) ──

/** A `PACKAGE` line of GET /catering/estimates/:id (also the public estimate). `itemId` is the package id. */
export function mapApiPackageEstimateLine(li: ApiEstimateDetail['lineItems'][number]): EstimateLineItem {
  return {
    id: li.id,
    type: 'package',
    isAddonItem: false,
    packageId: li.itemId ?? undefined,
    itemId: undefined,
    packageVariantId: li.packageVariantId ?? null,
    packageVariantName: li.packageVariantName ?? null,
    packageTagIds: li.packageTagIds ?? [],
    packageServes: li.servesCount ?? null,
    packageItems: normalizePackageItems(li.packageItems),
    sortOrder: li.sortOrder,
    isTaxExempt: !!li.isTaxExempt,
    name: li.name ?? '',
    quantity: parseFloat(li.quantity ?? '1') || 1,
    // The stored (possibly user-edited) price comes back here — parseFloat keeps a legitimate 0.
    unitPrice: parseFloat(li.unitPrice ?? '0') || 0,
    total: parseFloat(li.total ?? '0') || 0,
    notes: li.notes ?? '',
  };
}

/** A `PACKAGE` line of GET /catering/orders/:id. `itemId` is the package id; the FE keeps it in `packageId` and blanks `itemId`. */
export function mapApiPackageOrderLine(li: ApiOrderLineItem): OrderLineItem {
  return {
    id: li.id,
    type: 'package',
    packageId: li.itemId || undefined,
    itemId: '',
    itemName: li.itemName ?? '',
    pricingType: 'fixed',
    quantity: Number(li.quantity) || 0,
    unitPrice: Number(li.unitPrice) || 0,
    modifiers: [],
    lineTotal: Number(li.lineTotal) || 0,
    notes: li.notes ?? '',
    isAddonItem: false,
    sortOrder: li.sortOrder,
    isTaxExempt: !!li.isTaxExempt,
    packageVariantId: li.packageVariantId ?? null,
    packageVariantName: li.packageVariantName ?? null,
    packageTagIds: li.packageTagIds ?? [],
    packageServes: li.servesCount ?? null,
    packageItems: normalizePackageItems(li.packageItems),
  };
}

// ── FE → wire ──

/** Estimate line → `lineItems[]` entry of POST/PATCH /catering/estimates. */
export function toEstimateWireLine(li: EstimateLineItem, idx: number): UpdateLineItemBody {
  if (li.type === 'package' && li.packageId) {
    return {
      type: 'package',
      itemId: li.packageId,
      ...(li.packageVariantId ? { packageVariantId: li.packageVariantId } : {}),
      ...(li.packageServes !== undefined ? { servesCount: (li.packageServes ?? '').trim() } : {}),
      quantity: li.quantity,
      unitPrice: li.unitPrice,
      sortOrder: idx,
      ...(li.isTaxExempt ? { isTaxExempt: true } : {}),
      ...(li.notes?.trim() ? { notes: li.notes } : {}),
      packageItems: toLinePackageItems(li.packageItems),
    };
  }
  if (li.type === 'item' && li.itemId) {
    return {
      type: 'item',
      itemId: li.itemId,
      ...(li.tierId ? { tierId: li.tierId } : {}),
      name: li.name,
      quantity: li.quantity,
      sortOrder: idx,
      ...(li.isTaxExempt ? { isTaxExempt: true } : {}),
      ...(li.notes?.trim() ? { notes: li.notes } : {}),
    };
  }
  return {
    type: li.type === 'addon' ? 'addon' : 'custom',
    name: li.name,
    unitPrice: li.unitPrice,
    quantity: li.quantity,
    sortOrder: idx,
    ...(li.isTaxExempt ? { isTaxExempt: true } : {}),
    ...(li.notes?.trim() ? { notes: li.notes } : {}),
  };
}

/** Order line (`CateringOrder.items[]`) → `OrderPayloadLineItem`, used by both `addOrder` and `updateOrder`. */
export function toOrderItemPayload(li: OrderLineItem, idx: number): OrderPayloadLineItem {
  if (li.type === 'package' && li.packageId) {
    return {
      type: 'package',
      packageId: li.packageId,
      packageVariantId: li.packageVariantId || undefined,
      packageServes: li.packageServes,
      quantity: li.quantity,
      // Not `|| undefined`: the edited price must go out even when it is 0.
      unitPrice: li.unitPrice,
      notes: li.notes || undefined,
      sortOrder: idx,
      isTaxExempt: li.isTaxExempt || undefined,
      packageItems: li.packageItems ?? [],
      inDefaultTaxBase: li.inDefaultTaxBase,
    };
  }
  if (li.itemId) {
    return {
      itemId: li.itemId,
      tierId: li.tierId || undefined,
      name: li.itemName || undefined,
      unitPrice: li.unitPrice || undefined,
      quantity: li.quantity,
      notes: li.notes || undefined,
      sortOrder: idx,
      isTaxExempt: li.isTaxExempt || undefined,
      // Passed raw, not `|| undefined`: `false` is meaningful here (it means
      // "excluded from the order-level tax"), unlike the true-flags above.
      inDefaultTaxBase: li.inDefaultTaxBase,
    };
  }
  return {
    name: li.itemName.trim(),
    unitPrice: li.unitPrice,
    quantity: li.quantity,
    notes: li.notes || undefined,
    isAddon: li.isAddonItem || undefined,
    sortOrder: idx,
    isTaxExempt: li.isTaxExempt || undefined,
    inDefaultTaxBase: li.inDefaultTaxBase,
  };
}

/**
 * `OrderPayloadLineItem` → the exact `items[]` entry sent to POST/PATCH /catering/orders.
 * An explicit whitelist on purpose: it is what keeps FE-internal flags (`inDefaultTaxBase`) off the
 * wire. `idx` is the post-sort position so `sortOrder` always matches the array we send.
 */
export function toOrderWireItem(li: OrderPayloadLineItem, idx: number): ApiOrderWireItem & { sortOrder: number } {
  if (li.type === 'package' && li.packageId) {
    return {
      type: 'package',
      itemId: li.packageId,
      ...(li.packageVariantId ? { packageVariantId: li.packageVariantId } : {}),
      ...(li.packageServes !== undefined ? { servesCount: (li.packageServes ?? '').trim() } : {}),
      quantity: li.quantity,
      unitPrice: li.unitPrice ?? 0,
      ...(li.notes?.trim() ? { notes: li.notes } : {}),
      ...(li.isTaxExempt ? { isTaxExempt: true } : {}),
      packageItems: toLinePackageItems(li.packageItems),
      sortOrder: idx,
    };
  }
  return {
    ...(li.itemId ? { itemId: li.itemId } : {}),
    ...(li.tierId ? { tierId: li.tierId } : {}),
    quantity: li.quantity,
    ...(li.name?.trim() ? { name: li.name } : {}),
    ...(li.unitPrice ? { unitPrice: li.unitPrice } : {}),
    ...(li.notes?.trim() ? { notes: li.notes } : {}),
    ...(li.isAddon ? { isAddon: true } : {}),
    ...(li.isTaxExempt ? { isTaxExempt: true } : {}),
    sortOrder: idx,
  };
}
